/**
 * Weergave voor de publieke portalen (klantportaal + projectenportaal), Kordaro v4.
 * Puur: geen DOM/fetch. Server-codes blijven ongewijzigd; dit vertaalt ze enkel naar gewone woorden.
 *
 * Bronnen van de codes:
 * - Dossier.status            prisma/schema.prisma ("actief | afgerond | geannuleerd")
 * - Apparaat.status           klantportaal (VERWACHT … DEFECT), labels uit de vroegere KlantportaalPage
 * - Afspraak.status           klantportaalRouter ("voorstel | bevestigd | geweigerd | verlopen")
 * - Document.documentType     labels uit de vroegere KlantportaalPage
 * - Project.status            enum ProjectStatus (DRAFT … GEANNULEERD)
 * - Unit.bestelbonStatus      enum BestelbonFlowStatus, labels uit de vroegere ProjectPortaalPage
 */

export type Toon = 'neutraal' | 'info' | 'success' | 'warning' | 'danger';
export interface StatusWeergave { tekst: string; toon: Toon }

/** Onbekende code leesbaar maken: "in_uitvoering" → "In uitvoering", "GEPLAATST" → "Geplaatst". */
export function leesbareCode(code: string | null | undefined): string {
  const t = (code ?? '').trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').toLocaleLowerCase('nl-BE');
  return t ? t.charAt(0).toLocaleUpperCase('nl-BE') + t.slice(1) : '';
}

function kies(map: Record<string, StatusWeergave>, code: string | null | undefined): StatusWeergave {
  const sleutel = (code ?? '').trim();
  return map[sleutel] ?? map[sleutel.toLowerCase()] ?? map[sleutel.toUpperCase()] ?? { tekst: leesbareCode(sleutel) || 'Onbekend', toon: 'neutraal' };
}

const DOSSIER: Record<string, StatusWeergave> = {
  actief: { tekst: 'In uitvoering', toon: 'info' },
  in_uitvoering: { tekst: 'In uitvoering', toon: 'info' },
  afgerond: { tekst: 'Afgerond', toon: 'success' },
  geannuleerd: { tekst: 'Geannuleerd', toon: 'danger' },
};
export const dossierStatus = (code: string | null | undefined) => kies(DOSSIER, code);

const APPARAAT: Record<string, StatusWeergave> = {
  VERWACHT: { tekst: 'Verwacht', toon: 'neutraal' },
  BESTELD: { tekst: 'Besteld', toon: 'neutraal' },
  ONDERWEG: { tekst: 'Onderweg', toon: 'info' },
  BINNEN: { tekst: 'In de winkel', toon: 'info' },
  GELEVERD: { tekst: 'Geleverd', toon: 'success' },
  DEFECT: { tekst: 'Defect — de winkel volgt op', toon: 'danger' },
};
export const apparaatStatus = (code: string | null | undefined) => kies(APPARAAT, code);
/** Volgorde in de lijst: eerst wat nog moet komen, daarna geleverd. Defect bovenaan. */
export const APPARAAT_VOLGORDE: Record<string, number> = { DEFECT: -1, VERWACHT: 0, BESTELD: 1, ONDERWEG: 2, BINNEN: 3, GELEVERD: 4 };

const AFSPRAAK: Record<string, StatusWeergave> = {
  voorstel: { tekst: 'Wacht op uw keuze', toon: 'warning' },
  bevestigd: { tekst: 'Bevestigd', toon: 'success' },
  geweigerd: { tekst: 'Geen datum paste', toon: 'neutraal' },
  verlopen: { tekst: 'Verlopen', toon: 'neutraal' },
};
export const afspraakStatus = (code: string | null | undefined) => kies(AFSPRAAK, code);

const DOCTYPE: Record<string, string> = {
  keukenplan: 'Keukenplan',
  opmetingsverslag: 'Opmetingsverslag',
  opleveringsrapport: 'Opleveringsrapport',
  bestelbon: 'Bestelbon',
  offerte: 'Offerte',
  plaatsingsdossier: 'Plaatsingsdossier',
  bon_voor_levering: 'Bon voor levering',
};
export const documentType = (code: string | null | undefined) => (code ? DOCTYPE[code] ?? leesbareCode(code) : 'Document');

const PROJECT: Record<string, StatusWeergave> = {
  DRAFT: { tekst: 'In voorbereiding', toon: 'neutraal' },
  ACTIEF: { tekst: 'Lopend', toon: 'info' },
  GEPAUZEERD: { tekst: 'Gepauzeerd', toon: 'warning' },
  OPGELEVERD: { tekst: 'Opgeleverd', toon: 'success' },
  GEANNULEERD: { tekst: 'Geannuleerd', toon: 'danger' },
};
export const projectStatus = (code: string | null | undefined) => kies(PROJECT, code);

const BESTELBON: Record<string, string> = { BEVESTIGD: 'Bevestigd', ONTVANGEN: 'Ontvangen', OPGEMAAKT: 'Opgemaakt' };
/** GEEN of onbekend → null: de pagina toont dan "—" met reden. */
export const bestelbonStatus = (code: string | null | undefined): string | null => (code && BESTELBON[code]) || null;

/** Emoji uit (server)tekst halen: stap-iconen en activiteiten bevatten soms emoji; de v4-stijl toont er geen. */
export function zonderEmoji(tekst: string | null | undefined): string {
  return (tekst ?? '')
    .replace(/[\p{Extended_Pictographic}\u{FE0F}\u{200D}\u{20E3}]/gu, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Naam van een trajectstap-template: JSON {nl,fr,en} of gewone tekst; emoji weg. */
export function stapNaam(json: string | null | undefined): string {
  if (!json) return '';
  try {
    const p = JSON.parse(json) as Record<string, string>;
    return zonderEmoji(p.nl || p.fr || p.en || json);
  } catch {
    return zonderEmoji(json);
  }
}

// ── Leverdatum: verwacht / bevestigd / geleverd zijn drie verschillende beloftes (E01) ──
export interface StapLite { status: string; geplandeDatum: string | null; afgerondDatum: string | null; template: { naam: string } }
export interface AfspraakLite { status: string; bevestigdeDatum: string | null; trajectstap: { template: { naam: string } } }
export interface DatumRegel { soort: 'verwacht' | 'bevestigd' | 'afgerond' | 'onbekend'; label: string; datum: string | null; reden?: string }

const isSoort = (naam: string, soort: 'levering' | 'plaatsing') =>
  soort === 'levering' ? /lever/i.test(stapNaam(naam)) : /plaats|montage/i.test(stapNaam(naam));

/**
 * Wat we over de levering of plaatsing echt weten, in volgorde van zekerheid:
 * afgeronde trajectstap → "Geleverd op" / "Geplaatst op"; bevestigde afspraak → "Bevestigd voor";
 * enkel een dossierdatum → "Verwacht op"; niets → onbekend met reden (geen verzonnen datum).
 */
export function datumRegel(
  soort: 'levering' | 'plaatsing',
  dossierDatum: string | null,
  stappen: StapLite[],
  afspraken: AfspraakLite[],
): DatumRegel {
  const woorden = soort === 'levering'
    ? { klaar: 'Geleverd op', bevestigd: 'Levering bevestigd voor', verwacht: 'Levering verwacht op', onbekend: 'Levering' }
    : { klaar: 'Geplaatst op', bevestigd: 'Plaatsing bevestigd voor', verwacht: 'Plaatsing verwacht op', onbekend: 'Plaatsing' };
  const stap = stappen.find((s) => isSoort(s.template.naam, soort));
  if (stap?.status === 'completed') return { soort: 'afgerond', label: woorden.klaar, datum: stap.afgerondDatum };
  const bevestigd = afspraken
    .filter((a) => a.status === 'bevestigd' && a.bevestigdeDatum && isSoort(a.trajectstap.template.naam, soort))
    .sort((a, b) => (a.bevestigdeDatum! < b.bevestigdeDatum! ? -1 : 1))[0];
  if (bevestigd) return { soort: 'bevestigd', label: woorden.bevestigd, datum: bevestigd.bevestigdeDatum };
  const verwacht = dossierDatum ?? stap?.geplandeDatum ?? null;
  if (verwacht) return { soort: 'verwacht', label: woorden.verwacht, datum: verwacht };
  return { soort: 'onbekend', label: woorden.onbekend, datum: null, reden: 'Nog niet gepland' };
}

/** Initialen voor een naam ("Familie Testpersoon" → "FT"). */
export function initialen(naam: string | null | undefined): string {
  return (naam ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toLocaleUpperCase('nl-BE')).join('');
}
